'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { BackgroundGradient } from '@//components/BackgroundGradient';
import { Container } from '@//components/Container';
import { Heading } from '@//components/Heading';
import { WidgetForm } from '@//components/WidgetForm';
import AssignmentForm from '../AssignmentForm';
import styles from './AssignmentWidget.module.scss';

// Paths where the AssignmentWidget should not be rendered
const HIDDEN_PATHS = [
  // Internal routes
  '/sell-to-dealer/[...slug]',
  '/sell-to-dealer/created/[id]',
  '/profile',
  '/profile/[slug]',
  '/profile/[slug]/[id]',
  '/valuate-vehicle',
  '/valuate-vehicle/[...slug]',
  // Wayke routes
  '/salj-till-handlare/[...slug]',
  '/salj-till-handlare/skapad/[id]',
  '/profil',
  '/profil/[slug]',
  '/profil/[slug]/[id]',
  '/vardera-bil',
  '/vardera-bil/[...slug]',
  // Drive routes
  '/selg-til-forhandler/[...slug]',
  '/selg-til-forhandler/opprettet/[id]',
  '/profil',
  '/profil/[slug]',
  '/profil/[slug]/[id]',
  '/verdivurdere-bil',
  '/verdivurdere-bil/[...slug]',
];

const AssignmentWidgetClient: React.FC<{ marketMetadata: any }> = ({ marketMetadata }) => {
  const pathname = usePathname();
  const t = useTranslations('components.assignmentWidget');

  // Return null if current path matches any hidden path
  const shouldHide = HIDDEN_PATHS.some((hiddenPath) => {
    if (hiddenPath === pathname) return true;

    // Handle dynamic routes like /sell-to-dealer/[...slug] and /sell-to-dealer/created/[id]
    if (hiddenPath.includes('[')) {
      const pattern = hiddenPath
        .replace(/\[\.\.\.[\w]+\]/g, '.*') // [...slug] becomes .*
        .replace(/\[[\w]+\]/g, '[^/]+'); // [id] becomes [^/]+
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(pathname);
    }

    return false;
  });

  if (shouldHide) {
    return null;
  }

  const useAnimatedGradient = true;

  return (
    <article className={styles.widget}>
      <Container>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <Heading level={2} size="3xl">
              {t('heading')}
            </Heading>
            <p className={styles.description}>{t('description')}</p>
          </div>
          <WidgetForm>
            <AssignmentForm
              id="assignment-widget"
              assignmentPagePath={marketMetadata.pageLinks.assignmentPage}
              trackingPlacement="footer"
            />
          </WidgetForm>
          {useAnimatedGradient ? (
            <BackgroundGradient />
          ) : (
            <img
              src="/markets/wayke/gradient/large.jpg"
              alt="Backdround gradient"
              className={styles.gradient}
              aria-hidden="true"
              role="presentation"
            />
          )}
        </div>
      </Container>
    </article>
  );
};

export default AssignmentWidgetClient;
